import { useState } from "react";
import { Check, Loader2, Wrench, X } from "lucide-react";
import { api } from "@/lib/api";
import { randomUUID } from "@/lib/uuid";

type Props = {
  action: {
    actionType: string;
    summary: string;
    rationale?: string;
    targetIds: string[];
    parameters: Record<string, unknown>;
  };
};

export function ActionProposalCard({ action }: Props) {
  const [state, setState] = useState<"idle" | "working" | "approved" | "rejected" | "error">("idle");
  const [msg, setMsg] = useState<string>();

  async function approve() {
    setState("working");
    try {
      const id = randomUUID();
      const resp = await api.post(`/actions/${id}/approve`, action);
      setMsg(resp.data?.message ?? "approved");
      setState("approved");
    } catch (err) {
      setMsg(String(err));
      setState("error");
    }
  }

  async function reject() {
    setState("working");
    try {
      const id = randomUUID();
      await api.post(`/actions/${id}/reject`, action);
      setState("rejected");
    } catch (err) {
      setMsg(String(err));
      setState("error");
    }
  }

  const disabled = state !== "idle";

  return (
    <aside className="rounded-lg border border-datumcraft-warn/30 bg-datumcraft-warn/5 p-3">
      <div className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-datumcraft-warn">
        <Wrench size={13} aria-hidden />
        <span>Proposed action · {action.actionType}</span>
      </div>
      <p className="mt-1 text-sm font-medium text-datumcraft-navy">{action.summary}</p>
      {action.rationale && (
        <p className="mt-0.5 text-xs text-datumcraft-ink/90">{action.rationale}</p>
      )}
      {action.targetIds.length > 0 && (
        <p className="mt-1 text-2xs text-datumcraft-slate">
          Targets: <span className="font-mono">{action.targetIds.slice(0, 3).join(", ")}</span>
          {action.targetIds.length > 3 && ` +${action.targetIds.length - 3} more`}
        </p>
      )}
      <div className="mt-2 flex items-center gap-2 text-xs">
        <button
          onClick={approve}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1 font-medium text-white hover:bg-datumcraft-ink disabled:opacity-50"
        >
          {state === "working" ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          Approve
        </button>
        <button
          onClick={reject}
          disabled={disabled}
          className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/40 bg-white px-3 py-1 text-datumcraft-ink hover:bg-datumcraft-mist disabled:opacity-50"
        >
          <X size={12} /> Reject
        </button>
        {state === "approved" && (
          <span className="inline-flex items-center gap-1 text-datumcraft-success">
            <Check size={12} /> Approved{msg && msg !== "approved" ? ` — ${msg}` : ""}
          </span>
        )}
        {state === "rejected" && <span className="text-datumcraft-slate">Rejected</span>}
        {state === "error"    && <span className="text-datumcraft-danger">Error — {msg}</span>}
      </div>
    </aside>
  );
}
